import {inject, Injectable} from '@angular/core';
import {CmsApiService} from './cms-api.service';
import {LoggerService} from "./logger.service";

@Injectable({
  providedIn: 'root'
})
export class CmsQueueService {
  // Injections
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly logger: LoggerService = inject(LoggerService);

  private callQueue = {};
  private errorCallbackQueue = {}
  private cachedCmsData = {};
  private maxCachedTime = 10 * 60 * 1000;

  public async runCmsFnWithQueue(fn: any,
                                 params: any,
                                 noCache: boolean,
                                 callback: any,
                                 errorCallback: any,
                                 queueAllCallsToFunction: boolean = false) {
    const fnName = fn.name;
    const paramKey = this.getParamKey(fnName, params, queueAllCallsToFunction);
    const cachedData = this.getCachedData(paramKey);

    if (cachedData && !noCache) {
      callback(cachedData);
    } else {
      if (!this.getCallQueue(fnName, paramKey, true)) {
        this.createCallQueue(fnName, paramKey, callback, errorCallback);
        try {
          const data = await this.cms[fnName](params);
          this.setCachedData(paramKey, data);
          this.executeCallQueue(fnName, paramKey, data);
        } catch (e) {
          this.executeErrorCallbackQueue(fnName, paramKey, e);
          this.clearQueue(fnName, paramKey);
        }
      } else {
        this.addToCallQueue(fnName, paramKey, callback, errorCallback);
      }
    }
  }

  private getCachedData(paramKey: string) {
    let data = this.cachedCmsData[paramKey];
    if (data) {
      const thisTime = new Date().getTime();
      if (thisTime > data.$$cachedTime + this.maxCachedTime) {
        this.cachedCmsData[paramKey] = null;
        data = null;
      }
    }
    return data;
  }

  private setCachedData(paramKey: string, data: any) {
    if (data) {
      data.$$cachedTime = new Date().getTime();
    }
    this.cachedCmsData[paramKey] = data;
  }

  private getParamKey(fnName: any, params: any, queueAllCallsToFunction: boolean) {
    let res = fnName;
    if (!params || queueAllCallsToFunction) {
      return res;
    }
    for (const [key, value] of Object.entries(params)) {
      if (typeof value !== 'object' && value !== undefined && value !== null) {
        res += `:${key}:${value.toString()}`;
      } else if (value) {
        for (const [subKey, subVal] of Object.entries(value)) {
          if (typeof subVal !== 'object') {
            res += `:${key}:${subKey}:${subVal}`;
          } else if (Array.isArray(subVal)) {
            res += `:${key}:${subKey}:${subVal.join(':')}`;
          }
        }
      }
    }
    return res;
  }

  private createCallQueue(fnName: string, paramKey: string, callback: any, errorCallback: any) {
    if (callback) {
      if (!this.callQueue[fnName]) {
        this.callQueue[fnName] = {};
      }
      this.callQueue[fnName][paramKey] = {
        queue: [],
        time: new Date().getTime()
      };
    }
    if (errorCallback) {
      if (!this.errorCallbackQueue[fnName]) {
        this.errorCallbackQueue[fnName] = {};
      }
      this.errorCallbackQueue[fnName][paramKey] = [];
    }
    this.addToCallQueue(fnName, paramKey, callback, errorCallback);
  }

  private getCallQueue(functionName: string, paramKey: any, checkTimeout?: any) {
    let res: any[];
    const functionQueue = this.callQueue[functionName];
    if (functionQueue) {
      const paramQueue = functionQueue[paramKey];
      if (paramQueue) {
        if (checkTimeout &&
          paramQueue.time + 10000 < new Date().getTime()) {
          this.logger.warn(`Queue timed out for ${functionName} with params ${paramKey}`);
        } else {
          res = paramQueue.queue;
        }
      }
    }
    return res;
  }

  private addToCallQueue(fnName: string, paramKey: string, callback: any, errorCallback: any) {
    if (callback) {
      const queue = this.getCallQueue(fnName, paramKey);
      if (queue) {
        queue.push(callback);
      }
    }
    if (errorCallback) {
      this.errorCallbackQueue[fnName][paramKey].push(errorCallback);
    }
  }

  private clearQueue(fnName: string, paramKey: string) {
    this.callQueue[fnName][paramKey] = null;
    this.errorCallbackQueue[fnName][paramKey] = null;
  }

  private executeCallQueue(fnName: string, paramKey: any, data: any) {
    const queue = this.getCallQueue(fnName, paramKey);
    if (queue && queue.length > 0) {
      for (const callback of queue) {
        callback(data);
      }
    } else {
      this.logger.warn(`No queue for ${paramKey}`)
    }
    this.clearQueue(fnName, paramKey);
  }

  private executeErrorCallbackQueue(fnName: string, paramKey: string, exception: any) {
    const queue = this.errorCallbackQueue[fnName][paramKey];
    if (queue) {
      for (const errorCallback of queue) {
        errorCallback(exception);
      }
    }
  }
}
